import React from 'react';
import { MessageSquare, FileText, HelpCircle, Microscope } from 'lucide-react';

interface WelcomeScreenProps {
  onAskQuestions: () => void;
  onTreatmentPlan: () => void;
  onScanAnalysis?: () => void;
  onDeepResearch?: () => void;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ 
  onAskQuestions, 
  onTreatmentPlan,
  onScanAnalysis,
  onDeepResearch
}) => {
  // Count the number of visible buttons
  const visibleButtonsCount = [
    true, // Ask Questions is always shown
    true, // Treatment Planning is always shown
    !!onScanAnalysis, // Smart Scan Analysis is conditionally shown
    !!onDeepResearch  // Deep Research is conditionally shown
  ].filter(Boolean).length;

  // Determine grid layout based on visible buttons
  const gridClassName = visibleButtonsCount === 3 
    ? "grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" 
    : "grid grid-cols-1 md:grid-cols-2 gap-6 mb-8";

  return (
    <div className="h-full flex items-center justify-center bg-gradient-to-br from-medical-50 to-white">
      <div className="max-w-4xl mx-auto px-6 py-12 text-center">
        {/* Logo and Welcome */}
        <div className="mb-12">
          <img 
            src="/logo.png" 
            alt="OrthoMind" 
            className="w-24 h-24 mx-auto mb-6"
          />
          <h1 className="text-4xl font-semibold text-medical-900 mb-3">
            Welcome to OrthoMind
          </h1>
          <p className="text-lg text-medical-600">
            Your AI-powered orthodontic assistant for evidence-based treatment planning
          </p>
        </div>

        {/* Quick Actions */}
        <div className={gridClassName}>
          {/* Ask Questions */}
          <button
            onClick={onAskQuestions}
            className="group bg-white rounded-xl shadow-sm border border-medical-200 p-6 hover:shadow-md hover:border-primary-300 transition-all text-left"
          >
            <div className="flex items-start space-x-4">
              <div className="p-3 bg-primary-50 rounded-lg group-hover:bg-primary-100 transition-colors">
                <HelpCircle className="w-6 h-6 text-primary-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-medical-900 mb-2">
                  Ask Questions
                </h3>
                <p className="text-sm text-medical-600">
                  Get evidence-based answers from peer-reviewed orthodontic literature
                </p>
              </div>
            </div>
          </button>

          {/* Treatment Planning */}
          <button
            onClick={onTreatmentPlan}
            className="group bg-white rounded-xl shadow-sm border border-medical-200 p-6 hover:shadow-md hover:border-primary-300 transition-all text-left"
          >
            <div className="flex items-start space-x-4">
              <div className="p-3 bg-green-50 rounded-lg group-hover:bg-green-100 transition-colors">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-medical-900 mb-2">
                  Draft Treatment Plan
                </h3>
                <p className="text-sm text-medical-600">
                  Create comprehensive treatment plans with AI assistance
                </p>
              </div>
            </div>
          </button>

          {/* Smart Scan Analysis */}
          {onScanAnalysis && (
            <button
              onClick={onScanAnalysis}
              className="group bg-white rounded-xl shadow-sm border border-medical-200 p-6 hover:shadow-md hover:border-primary-300 transition-all text-left"
            >
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                  <Microscope className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-medical-900 mb-2">
                    Smart Scan Analysis
                  </h3>
                  <p className="text-sm text-medical-600">
                    Upload dental scans for AI-powered diagnostic analysis
                  </p>
                </div>
              </div>
            </button>
          )}

          {/* Deep Research */}
          {onDeepResearch && (
            <button
              onClick={onDeepResearch}
              className="group bg-white rounded-xl shadow-sm border border-medical-200 p-6 hover:shadow-md hover:border-primary-300 transition-all text-left"
            >
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-purple-50 rounded-lg group-hover:bg-purple-100 transition-colors">
                  <MessageSquare className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-medical-900 mb-2">
                    Deep Research
                  </h3>
                  <p className="text-sm text-medical-600">
                    Comprehensive research synthesis from orthodontic journals
                  </p>
                </div>
              </div>
            </button>
          )}
        </div>

        {/* Quick Tips */}
        <div className="bg-medical-50 rounded-lg p-6 text-left">
          <h3 className="font-semibold text-medical-900 mb-3">Quick Tips:</h3>
          <ul className="space-y-2 text-sm text-medical-600">
            <li>• Type your orthodontic questions to get evidence-based answers</li>
            <li>• Upload patient scans for AI-powered analysis and recommendations</li>
            <li>• Generate comprehensive treatment plans with clinical protocols</li>
            <li>• Access peer-reviewed research with our Deep Research feature</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;